import { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Settings2 } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiError } from './shared';
import { useShiftSettings } from './useShiftSettings';

const codes = [['AM', 'AM — Matin'], ['PM', 'PM — Après-midi / Soir']];
export const ShiftSettingsButton = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  return <>
    <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="shift-settings-open"><Settings2 className="w-4 h-4" />Paramètres des horaires DSP</Button>
    {open && <ShiftSettingsDialog onClose={() => setOpen(false)} onSaved={onSaved} />}
  </>;
};

export const ShiftSettingsDialog = ({ onClose, onSaved }) => {
  const settings = useShiftSettings();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setForm({ AM: { ...settings.AM }, PM: { ...settings.PM } }); }, [settings]);
  const update = (code, key, value) => setForm(f => ({ ...f, [code]: { ...f[code], [key]: value } }));
  const save = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const { data } = await axios.put(`${API}/internal-staff/shift-settings`, { AM: form.AM, PM: form.PM, baseline_updated_at: settings.updated_at || null }, { withCredentials: true });
      onSaved?.(data); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  return <Dialog open onOpenChange={o => { if (!o && !busy) onClose(); }}><DialogContent className="w-[calc(100%_-_2rem)] max-w-md" data-testid="shift-settings-dialog">
    <DialogHeader><DialogTitle>Paramètres des horaires DSP</DialogTitle><DialogDescription>Horaires par défaut des créneaux AM et PM. Un changement s’applique aux nouvelles programmations uniquement : les services déjà planifiés et les historiques ne sont pas réécrits.</DialogDescription></DialogHeader>
    {form && <form className="space-y-5" onSubmit={save} data-testid="shift-settings-form">
      {codes.map(([code, title]) => <fieldset className="space-y-2 border rounded-md p-3" key={code} data-testid={`shift-settings-${code}`}>
        <legend className="px-1 text-sm font-semibold">{title}</legend>
        <label className="text-sm block">Libellé<Input maxLength={40} required value={form[code].label} onChange={e => update(code, 'label', e.target.value)} data-testid={`shift-settings-${code}-label`} /></label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-sm">Début<Input type="time" required value={form[code].start_time} onChange={e => update(code, 'start_time', e.target.value)} data-testid={`shift-settings-${code}-start`} /></label>
          <label className="text-sm">Fin<Input type="time" required value={form[code].end_time} onChange={e => update(code, 'end_time', e.target.value)} data-testid={`shift-settings-${code}-end`} /></label>
        </div>
        {form[code].end_time < form[code].start_time && <p className="text-xs text-amber-800">Fin le lendemain (service de nuit).</p>}
      </fieldset>)}
      {settings.updated_at && <p className="text-xs text-slate-500" data-testid="shift-settings-updated">Dernière modification : {new Date(settings.updated_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}{settings.updated_by_name ? ` par ${settings.updated_by_name}` : ''}</p>}
      {error && <p role="alert" className="text-sm text-red-700" data-testid="shift-settings-error">{error}</p>}
      <div className="flex gap-2 justify-end"><Button type="button" variant="outline" disabled={busy} onClick={onClose} data-testid="shift-settings-cancel">Annuler</Button><Button type="submit" disabled={busy} data-testid="shift-settings-save"><Save className="w-4 h-4" />Enregistrer</Button></div>
    </form>}
  </DialogContent></Dialog>;
};
